/**
 * Frontend Date Utilities
 *
 * Problem: `new Date('2026-02-13')` creates midnight UTC.
 * In Eastern Time, that displays as Feb 12 at 7pm — the wrong day.
 *
 * Solution: Append 'T12:00:00' to date-only strings so the Date is noon UTC,
 * which always displays as the correct calendar date in any US timezone.
 */

/**
 * Safely parse a date string (YYYY-MM-DD) into a Date that displays correctly.
 * If the string already has a time component, it's passed through unchanged.
 */
export function parseDate(dateStr: string): Date {
  if (!dateStr) return new Date(NaN);
  // Date-only strings (YYYY-MM-DD) get noon UTC to avoid off-by-one display
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    return new Date(dateStr + 'T12:00:00');
  }
  return new Date(dateStr);
}

/**
 * Format a date string (YYYY-MM-DD) for display using the user's locale.
 * Handles the midnight-UTC off-by-one bug.
 */
export function formatDate(dateStr: string, options?: Intl.DateTimeFormatOptions): string {
  const date = parseDate(dateStr);
  if (isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-US', options);
}

/**
 * Calculate nights between two date strings (YYYY-MM-DD).
 * Uses simple string math to avoid timezone issues entirely.
 */
export function nightsBetween(startDate: string, endDate: string): number {
  const start = new Date(startDate + 'T00:00:00Z');
  const end = new Date(endDate + 'T00:00:00Z');
  return Math.round((end.getTime() - start.getTime()) / 86400000);
}

/**
 * Get today's date as YYYY-MM-DD in the browser's local timezone.
 *
 * Use this instead of `new Date().toISOString().split('T')[0]` which
 * returns the UTC date (wrong after 7pm ET / midnight UTC).
 */
export function getToday(): string {
  return new Intl.DateTimeFormat('en-CA').format(new Date());
}
